import React, { useEffect, useState } from "react";
import { DownloadSimple, X, DeviceMobile } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";

/**
 * PWA install banner — captures `beforeinstallprompt` and shows a soft banner
 * after 30s on a returning visitor (skip once user dismisses or installs).
 *
 * Also handles iOS instructions (Safari doesn't fire `beforeinstallprompt`).
 */
const DISMISS_KEY = "mc68_pwa_dismissed_at";
const DISMISS_DAYS = 14;
const DELAY_MS = 30_000;

export default function PwaInstallBanner() {
  const { t } = useI18n();
  const [promptEvent, setPromptEvent] = useState(null);
  const [show, setShow] = useState(false);
  const [iosShow, setIosShow] = useState(false);

  useEffect(() => {
    // Register service worker (best-effort)
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => { /* silent */ });
      });
    }

    // Was dismissed recently?
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    if (dismissedAt && daysSince < DISMISS_DAYS) return;

    // Already installed (display-mode standalone)
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return; // iOS legacy

    // Capture native prompt event (Chrome/Edge/Android)
    const handler = (e) => {
      e.preventDefault();
      setPromptEvent(e);
      // Delay for non-intrusiveness
      setTimeout(() => setShow(true), DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari has no prompt — show instructions instead, after delay
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIos && isSafari) {
      setTimeout(() => setIosShow(true), DELAY_MS);
    }

    window.addEventListener("appinstalled", () => {
      setShow(false); setIosShow(false); setPromptEvent(null);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    });

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setShow(false); setIosShow(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const install = async () => {
    if (!promptEvent) return;
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === "accepted") {
      setShow(false);
    } else {
      dismiss();
    }
    setPromptEvent(null);
  };

  if (!show && !iosShow) return null;

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-24 sm:right-4 z-50 max-w-[360px] bg-card border border-primary/40 rounded-sm shadow-lg p-4 animate-fade-in" data-testid="pwa-install-banner">
      <button onClick={dismiss} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground" aria-label={t.pwa.close}>
        <X size={14}/>
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 shrink-0 bg-primary/10 text-primary rounded-sm grid place-items-center">
          <DeviceMobile size={20} weight="duotone"/>
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold text-sm mb-1">{t.pwa.title}</div>
          {show && promptEvent && (
            <>
              <div className="text-xs text-muted-foreground mb-3">
                {t.pwa.description}
              </div>
              <button onClick={install} className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="pwa-install-btn">
                <DownloadSimple size={14} weight="bold"/> {t.pwa.install_now}
              </button>
            </>
          )}
          {iosShow && (
            <div className="text-xs text-muted-foreground space-y-1">
              <p>{t.pwa.ios_intro}</p>
              <ol className="list-decimal pl-4 space-y-0.5">
                <li>{t.pwa.ios_step1_prefix}<b>{t.pwa.ios_step1_bold}</b>{t.pwa.ios_step1_suffix}</li>
                <li>{t.pwa.ios_step2_prefix}<b>{t.pwa.ios_step2_bold}</b></li>
                <li>{t.pwa.ios_step3_prefix}<b>{t.pwa.ios_step3_bold}</b></li>
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
